import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { formatCurrency } from '@/lib/utils';

const TIER_COLORS = [
  'bg-blue-100',
  'bg-blue-400',
  'bg-blue-600',
  'bg-blue-800',
  'bg-blue-950',
];

function fmt(n: number) {
  return formatCurrency(Math.round(n), 'CAD');
}

export function CalculatorForm() {
  const [burn, setBurn] = useState(13500);
  const [mrr, setMrr] = useState(30000);

  // Local marginal calc (burn-based tiers).
  // TODO: wire to trpc.commission.simulate once API is deployed.
  const localResult = useMemo(() => {
    const tiers = [
      { label: '$0 → 1x burn', lo: 0, hi: burn, rate: 0.10 },
      { label: '1x → 2x burn', lo: burn, hi: burn * 2, rate: 0.15 },
      { label: '2x → 3x burn', lo: burn * 2, hi: burn * 3, rate: 0.20 },
      { label: '3x → 5x burn', lo: burn * 3, hi: burn * 5, rate: 0.22 },
      { label: '5x+ burn', lo: burn * 5, hi: Infinity, rate: 0.25 },
    ];

    let monthlyComm = 0;
    const breakdown = tiers.map((t) => {
      const inBand = Math.max(0, Math.min(mrr, t.hi) - t.lo);
      const commission = inBand * t.rate;
      monthlyComm += commission;
      return { label: t.label, revenueInRange: inBand, rate: t.rate, commission };
    });

    const quarterlyComm = monthlyComm * 3;
    const annualBonus = quarterlyComm * 4;
    const effectiveRate = mrr > 0 ? monthlyComm / mrr : 0;

    return { breakdown, monthlyComm, quarterlyComm, annualBonus, effectiveRate };
  }, [burn, mrr]);

  const { quarterlyComm, annualBonus, effectiveRate, breakdown } = localResult;
  const totalMrr = mrr || 1;

  return (
    <div className="space-y-6">
      {/* Sliders */}
      <Card>
        <CardContent className="space-y-6 pt-6">
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Monthly burn</Label>
              <span className="text-sm font-semibold">{fmt(burn)}</span>
            </div>
            <Slider
              min={5000}
              max={30000}
              step={500}
              value={[burn]}
              onValueChange={([v]) => setBurn(v!)}
            />
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label>Team MRR</Label>
              <span className="text-sm font-semibold">{fmt(mrr)}</span>
            </div>
            <Slider
              min={0}
              max={100000}
              step={500}
              value={[mrr]}
              onValueChange={([v]) => setMrr(v!)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Quarterly commission</p>
            <p className="mt-1 text-2xl font-bold">{fmt(quarterlyComm)}</p>
            <p className="text-xs text-muted-foreground">paid 30 days after quarter close</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Annual bonus</p>
            <p className="mt-1 text-2xl font-bold">{fmt(annualBonus)}</p>
            <p className="text-xs text-muted-foreground">quarterly × 4</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Total annual comp</p>
            <p className="mt-1 text-2xl font-bold">{fmt(60000 + annualBonus)}</p>
            <p className="text-xs text-muted-foreground">
              {(effectiveRate * 100).toFixed(1)}% effective rate
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tier breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Tier Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
                <th className="pb-2 text-left font-medium">Band</th>
                <th className="pb-2 text-left font-medium">Rate</th>
                <th className="pb-2 text-left font-medium">MRR in band</th>
                <th className="pb-2 text-right font-medium">Monthly commission</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((row, i) => {
                const active = row.revenueInRange > 0;
                return (
                  <tr
                    key={i}
                    className={active ? 'bg-blue-50 font-medium text-blue-700' : 'text-muted-foreground'}
                  >
                    <td className="py-2">{row.label}</td>
                    <td className="py-2">{(row.rate * 100).toFixed(0)}%</td>
                    <td className="py-2">{fmt(row.revenueInRange)}</td>
                    <td className="py-2 text-right">{fmt(row.commission)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* MRR allocation bar */}
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">MRR Allocation</p>
            <div className="flex h-3 overflow-hidden rounded-full bg-muted">
              {breakdown.map((row, i) =>
                row.revenueInRange > 0 ? (
                  <div
                    key={i}
                    className={`h-full ${TIER_COLORS[i]}`}
                    style={{ width: `${(row.revenueInRange / totalMrr) * 100}%` }}
                  />
                ) : null,
              )}
            </div>
            <div className="flex flex-wrap gap-3">
              {breakdown.map((row, i) =>
                row.revenueInRange > 0 ? (
                  <div key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <div className={`h-2.5 w-2.5 rounded-sm ${TIER_COLORS[i]}`} />
                    {row.label} ({(row.rate * 100).toFixed(0)}%)
                  </div>
                ) : null,
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground">
        Commission = monthly rate × 3 months, paid as a quarterly bonus.
        Tier thresholds reset at the start of each quarter based on prior-quarter burn.
      </p>
    </div>
  );
}
